'use client';

import { Box, styled } from '@terra/ui-styled-system';
import type { ComponentProps } from 'react';
import { forwardRef, useEffect, useRef } from 'react';

export type ScrollProps = ComponentProps<typeof ScrollableContainer> & {
  autoScroll?: boolean;
};

const OuterContainer = styled(Box, {
  base: {
    position: 'relative',
    width: '100%',
    height: '100%',
    minWidth: 0,
    minHeight: 0,
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
  },
});

const ScrollableContainer = styled(Box, {
  base: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    position: 'relative',
    WebkitOverflowScrolling: 'touch',
  },
  variants: {
    orientation: {
      vertical: {
        overflowY: 'auto',
        overflowX: 'hidden',
      },
      horizontal: {
        overflowY: 'hidden',
        overflowX: 'auto',
      },
      both: {
        overflow: 'auto',
      },
    },
    showScrollbar: {
      false: {
        scrollbarWidth: 'none',
        '&::-webkit-scrollbar': {
          display: 'none',
        },
      },
      true: {
        scrollbarWidth: 'thin',
        scrollbarColor: 'var(--colors-gray-300) var(--colors-gray-100)',
      },
      hover: {
        scrollbarWidth: 'none',
        _hover: {
          scrollbarWidth: 'thin',
          scrollbarColor: 'var(--colors-gray-300) var(--colors-gray-100)',
        },
      },
    },
  },
  defaultVariants: {
    orientation: 'vertical',
    showScrollbar: false,
  },
});

export const Scroll = forwardRef<HTMLDivElement, ScrollProps>(
  ({ children, orientation, showScrollbar, autoScroll, ...props }, ref) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const observerRef = useRef<MutationObserver | null>(null);
    const isAtBottomRef = useRef(true);

    useEffect(() => {
      let lastScrollHeight = 0;

      function debounce(callback: () => void, delay: number) {
        let timeout: number | undefined = undefined;
        const fn = () => {
          window.clearTimeout(timeout);
          timeout = window.setTimeout(() => callback(), delay);
        };
        fn.cancel = () => window.clearTimeout(timeout);
        return fn;
      }

      function isScrollAtBottom(
        element: HTMLDivElement,
        threshold = 4,
      ): boolean {
        const { scrollTop, scrollHeight, clientHeight } = element;
        return scrollHeight - scrollTop - clientHeight <= threshold;
      }

      function handleScroll() {
        if (containerRef.current) {
          isAtBottomRef.current = isScrollAtBottom(containerRef.current);
        }
      }

      if (!autoScroll || !containerRef.current) return;

      const container = containerRef.current;

      container.addEventListener('scroll', handleScroll, { passive: true });

      const scrollToBottom = debounce(() => {
        requestAnimationFrame(() => {
          if (containerRef.current && isAtBottomRef.current) {
            const newScrollHeight = containerRef.current.scrollHeight;
            if (newScrollHeight > lastScrollHeight) {
              lastScrollHeight = newScrollHeight;
              containerRef.current.scrollTo({
                top: lastScrollHeight,
                behavior: 'auto',
              });
            }
          }
        });
      }, 100);

      observerRef.current = new MutationObserver((mutations) => {
        if (mutations.some((mutation) => mutation.type === 'childList')) {
          scrollToBottom();
        }
      });

      observerRef.current.observe(containerRef.current, {
        childList: true,
        subtree: true,
      });

      return () => {
        observerRef.current?.disconnect();
        scrollToBottom.cancel();
        container.removeEventListener('scroll', handleScroll);
      };
    }, [autoScroll]);

    return (
      <OuterContainer data-scroll-outer>
        <ScrollableContainer
          data-scroll-container
          ref={(node) => {
            if (typeof ref === 'function') {
              ref(node);
            } else if (ref) {
              ref.current = node;
            }
            (
              containerRef as React.MutableRefObject<HTMLDivElement | null>
            ).current = node;
          }}
          orientation={orientation}
          showScrollbar={showScrollbar}
          {...props}
        >
          {children}
        </ScrollableContainer>
      </OuterContainer>
    );
  },
);

Scroll.displayName = 'Scroll';
